import React from 'react';

import KeyChip from '@dashboard/components/KeyChip';
import LoadingIndicator from '@dashboard/components/loading/LoadingIndicator';
import BaseSettingCard from '@dashboard/components/platform-settings/BaseSettingCard';
import {
  useCreateSvixCustomerPortalUrlMutation,
  usePlatformBillingInformationQuery,
  useWebhookEndpointsQuery,
  WaypointFeatureTier,
} from '@dashboard/generated/wp/platform';
import { LaunchOutlined, TipsAndUpdatesOutlined } from '@mui/icons-material';
import { Alert, Button, Link, Stack, Typography } from '@mui/material';

import WebhookCard from './WebhookCard';

export default function WebhookDefinitionsList() {
  const [createPortalUrl, { loading }] = useCreateSvixCustomerPortalUrlMutation();
  const { data } = useWebhookEndpointsQuery();
  const { data: platformData } = usePlatformBillingInformationQuery();

  const webhookEndpoints = data?.WebhookEndpoints;
  const p = platformData?.PlatformBillingInformation;

  if (webhookEndpoints === undefined || p === undefined) {
    return null;
  }

  const onManageBillingClick = async () => {
    const { data } = await createPortalUrl();
    if (!data?.createSvixCustomerPortalUrl) {
      return;
    }
    try {
      const newWindow = window.open(data.createSvixCustomerPortalUrl, '_blank');
      if (!newWindow) {
        window.location.href = data.createSvixCustomerPortalUrl;
      }
    } catch (e) {
      window.location.href = data.createSvixCustomerPortalUrl;
    }
  };

  const renderContent = () => {
    const models = data?.WebhookEndpoints.nodes;
    if (models === undefined) {
      return <LoadingIndicator />;
    }
    if (models.length === 0) {
      return (
        <Typography color="text.secondary">
          Create webhook endpoints and subscribe to email delivery events. Click 'Manage' to get started.
        </Typography>
      );
    }
    return models.map((item) => <WebhookCard key={item.id} item={item} />);
  };

  const renderLimitedMessage = () => {
    switch (p.activeWaypointTier) {
      case WaypointFeatureTier.Free:
      case WaypointFeatureTier.Sample:
      case WaypointFeatureTier.Sandbox:
      case WaypointFeatureTier.Starter:
        return (
          <Alert variant="outlined" severity="info" icon={<TipsAndUpdatesOutlined />}>
            <Typography>
              <Link
                href="https://www.usewaypoint.com/docs/webhooks#send-webhook-events"
                fontWeight="medium"
                target="_blank"
              >
                Upgrade to unlock additional event types
              </Link>{' '}
              like <KeyChip label="EMAIL_MESSAGE.OPENED" size="sm" /> and{' '}
              <KeyChip label="EMAIL_MESSAGE.BOUNCED" size="sm" />.
            </Typography>
          </Alert>
        );
      case WaypointFeatureTier.Growth:
      case WaypointFeatureTier.Pro:
      case WaypointFeatureTier.Unlimited:
        return null;
    }
  };

  return (
    <BaseSettingCard
      title="Webhooks"
      helpUrl="https://www.usewaypoint.com/docs/webhooks"
      actions={
        <Button
          disabled={loading}
          onClick={onManageBillingClick}
          variant="outlined"
          endIcon={<LaunchOutlined style={{ width: 16, height: 16 }} />}
        >
          Manage
        </Button>
      }
    >
      <Stack spacing={3}>
        {renderContent()}
        {renderLimitedMessage()}
      </Stack>
    </BaseSettingCard>
  );
}
